import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { eventBus } from '../events/eventBus';
import type {
  ProjectStatusUpdate,
  NewProjectStatusUpdate,
  AttachedTaskInfo
} from '../types/projectStatusUpdate';

interface ProjectStatusUpdateStore {
  // State
  statusUpdates: ProjectStatusUpdate[];
  loading: boolean;
  error: string | null;

  // Actions
  fetchStatusUpdates: (projectId: string) => Promise<void>;
  createStatusUpdate: (
    data: NewProjectStatusUpdate,
    attachedTaskIds: string[]
  ) => Promise<ProjectStatusUpdate | null>;
  // Crea uno status update a partire da un thread Gmail collegato (eredita health/avanzamento correnti)
  createEmailStatusUpdate: (
    projectId: string,
    payload: { gmailThreadId: string; subject: string; fromEmail: string; fromName?: string | null; snippet?: string | null; messageCount: number }
  ) => Promise<ProjectStatusUpdate | null>;
  updateStatusUpdate: (
    id: string,
    data: Partial<NewProjectStatusUpdate>,
    attachedTaskIds?: string[]
  ) => Promise<ProjectStatusUpdate | null>;
  deleteStatusUpdate: (id: string) => Promise<void>;

  // Utility
  getEligibleTasks: (projectId: string) => Promise<AttachedTaskInfo[]>;
  clearStatusUpdates: () => void;
}

export const useProjectStatusUpdateStore = create<ProjectStatusUpdateStore>((set, get) => ({
  statusUpdates: [],
  loading: false,
  error: null,

  clearStatusUpdates: () => {
    set({ statusUpdates: [], error: null });
  },

  fetchStatusUpdates: async (projectId: string) => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('Organization not found');

      const { data, error } = await supabase
        .from('project_status_updates')
        .select(`
          *,
          creator:user_profiles!project_status_updates_created_by_fkey(
            id, email, full_name, avatar_url
          ),
          attached_tasks:project_status_update_tasks(
            id,
            status_update_id,
            task_id,
            added_at,
            task:tasks(
              id,
              title,
              status,
              priority,
              completed_at,
              review_at,
              assigned_to,
              assignee:user_profiles!tasks_assigned_to_fkey(
                id, full_name, email
              ),
              comments:task_comments(id, created_at)
            )
          )
        `)
        .eq('project_id', projectId)
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ statusUpdates: data || [] });
    } catch (error) {
      console.error('Error fetching status updates:', error);
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createStatusUpdate: async (data, attachedTaskIds) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) throw new Error('Organization not found');

      // Create the status update
      const { data: newUpdate, error: insertError } = await supabase
        .from('project_status_updates')
        .insert({
          ...data,
          organization_id: profile.organization_id,
          created_by: userData.user.id
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Attach tasks if any
      if (attachedTaskIds.length > 0) {
        const taskAttachments = attachedTaskIds.map(taskId => ({
          status_update_id: newUpdate.id,
          task_id: taskId,
          organization_id: profile.organization_id
        }));

        const { error: attachError } = await supabase
          .from('project_status_update_tasks')
          .insert(taskAttachments);

        if (attachError) {
          console.error('Error attaching tasks:', attachError);
        }
      }

      // Refresh the list
      await get().fetchStatusUpdates(data.project_id);

      // Emit evento per notifica
      try {
        const { data: projectData } = await supabase
          .from('projects')
          .select('name')
          .eq('id', data.project_id)
          .single();

        eventBus.emitEvent({
          type: 'project:status_update',
          payload: {
            projectId: data.project_id,
            projectName: projectData?.name || '',
            statusUpdateId: newUpdate.id,
            healthStatus: data.health_status,
            isEdit: false,
          }
        });
      } catch (emitErr) {
        console.error('Error emitting project:status_update:', emitErr);
      }

      return newUpdate;
    } catch (error) {
      console.error('Error creating status update:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  createEmailStatusUpdate: async (projectId, payload) => {
    const esc = (v: string) => v
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    // Eredita health_status e avanzamento dall'ultimo update del progetto (se esiste)
    const { data: last } = await supabase
      .from('project_status_updates')
      .select('health_status, completion_percentage')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    const sender = payload.fromName?.trim() || payload.fromEmail;
    const gmailUrl = `https://mail.google.com/mail/u/0/#all/${payload.gmailThreadId}`;
    const countLabel = payload.messageCount === 1 ? '1 messaggio' : `${payload.messageCount} messaggi`;
    const description =
      `<p>📧 <strong>${esc(payload.subject || '(Nessun oggetto)')}</strong></p>` +
      `<p>Email da: ${esc(sender)} &lt;${esc(payload.fromEmail)}&gt; · ${countLabel}</p>` +
      (payload.snippet ? `<p>${esc(payload.snippet)}</p>` : '') +
      `<p><a href="${gmailUrl}" target="_blank" rel="noopener noreferrer">Apri in Gmail</a></p>`;

    return get().createStatusUpdate({
      project_id: projectId,
      description,
      health_status: last?.health_status || 'on_track',
      completion_percentage: last?.completion_percentage ?? 0,
    }, []);
  },

  updateStatusUpdate: async (id, data, attachedTaskIds) => {
    try {
      const { data: updateResult, error } = await supabase
        .from('project_status_updates')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Handle attached tasks if provided
      if (attachedTaskIds !== undefined) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('id', (await supabase.auth.getUser()).data.user?.id)
          .single();

        // Remove all existing associations
        await supabase
          .from('project_status_update_tasks')
          .delete()
          .eq('status_update_id', id);

        // Add new ones
        if (attachedTaskIds.length > 0) {
          const taskAttachments = attachedTaskIds.map(taskId => ({
            status_update_id: id,
            task_id: taskId,
            organization_id: profile?.organization_id
          }));

          await supabase
            .from('project_status_update_tasks')
            .insert(taskAttachments);
        }
      }

      // Refresh if we have project_id
      if (updateResult.project_id) {
        await get().fetchStatusUpdates(updateResult.project_id);
      }

      // Emit evento per notifica
      try {
        const { data: projectData } = await supabase
          .from('projects')
          .select('name')
          .eq('id', updateResult.project_id)
          .single();

        eventBus.emitEvent({
          type: 'project:status_update',
          payload: {
            projectId: updateResult.project_id,
            projectName: projectData?.name || '',
            statusUpdateId: id,
            healthStatus: data.health_status || updateResult.health_status,
            isEdit: true,
          }
        });
      } catch (emitErr) {
        console.error('Error emitting project:status_update:', emitErr);
      }

      return updateResult;
    } catch (error) {
      console.error('Error updating status update:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteStatusUpdate: async (id) => {
    try {
      // Get data before deleting
      const statusUpdate = get().statusUpdates.find(su => su.id === id);
      const projectId = statusUpdate?.project_id;

      // Audit log
      if (statusUpdate) {
        const { data: userData } = await supabase.auth.getUser();
        if (userData.user) {
          await supabase.from('audit_logs').insert({
            organization_id: statusUpdate.organization_id,
            user_id: userData.user.id,
            entity_type: 'project_status_update',
            entity_id: id,
            entity_name: `Status update ${statusUpdate.is_ai_generated ? '(AI)' : ''} - ${statusUpdate.health_status} ${statusUpdate.completion_percentage}%`,
            action_type: 'delete',
            project_id: statusUpdate.project_id,
            metadata: {
              description: statusUpdate.description,
              next_steps: statusUpdate.next_steps,
              is_ai_generated: statusUpdate.is_ai_generated,
              created_by: statusUpdate.created_by,
              created_at: statusUpdate.created_at,
            }
          });
        }
      }

      const { error } = await supabase
        .from('project_status_updates')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Update local state
      const statusUpdates = get().statusUpdates.filter(su => su.id !== id);
      set({ statusUpdates });

      // Optionally refresh if needed
      if (projectId) {
        await get().fetchStatusUpdates(projectId);
      }
    } catch (error) {
      console.error('Error deleting status update:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  // Get all tasks for attaching to status update
  getEligibleTasks: async (projectId) => {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select(`
          id,
          title,
          status,
          priority,
          completed_at,
          review_at,
          assigned_to,
          updated_at,
          assignee:user_profiles!tasks_assigned_to_fkey(id, full_name, email),
          comments:task_comments(id, created_at)
        `)
        .eq('project_id', projectId)
        .eq('is_header', false)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      // Sort by status priority: in_progress > todo > review > completed
      // Then by updated_at (most recent first)
      const statusPriority: Record<string, number> = {
        'in_progress': 0,
        'todo': 1,
        'review': 2,
        'completed': 3
      };

      const sortedData = (data || []).sort((a, b) => {
        // First priority: status
        const aPriority = statusPriority[a.status] ?? 4;
        const bPriority = statusPriority[b.status] ?? 4;
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        // Second priority: updated_at (most recent first)
        const aUpdated = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const bUpdated = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return bUpdated - aUpdated;
      });

      return sortedData as AttachedTaskInfo[];
    } catch (error) {
      console.error('Error fetching eligible tasks:', error);
      return [];
    }
  }
}));
